import {
  Box,
  VStack,
  Button,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Image,
  HStack,
  Collapse,
  Icon,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { renderMenuIcon } from '@/lib/icons/menuIcons'
import { useEffect, useState, useMemo, useCallback, memo, useRef } from 'react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

interface NavigationItemDef {
  name: string
  href: string
  icon: string
}

interface SidebarProps {
  items?: NavigationItemDef[]
  isOpen?: boolean
  onClose?: () => void
  /** Desktop collapse state (md+) */
  isCollapsed?: boolean
}

type SidebarLogoProps = {
  logoUrl: string
  appName: string
  isCollapsed?: boolean
  /** Retain support-admin background */
  bg: string
}

const SidebarLogo = memo(({ logoUrl, appName, isCollapsed, bg }: SidebarLogoProps) => {
  return (
    <Box
      pt={isCollapsed ? 2 : 4}
      px={isCollapsed ? 2 : 5}
      pb={isCollapsed ? 1 : 4}
      flexShrink={0}
      bg={bg}
      position="sticky"
      top={0}
      zIndex={1}
    >
      <HStack
        spacing={isCollapsed ? 0 : 2}
        align="center"
        overflow="hidden"
        w="full"
        justifyContent={isCollapsed ? 'center' : 'flex-start'}
      >
        <Image
          key={logoUrl}
          src={logoUrl}
          alt="ALS Logo"
          height="32px"
          width={isCollapsed ? '32px' : 'auto'}
          objectFit="contain"
          loading="eager"
          flexShrink={0}
          onError={(e) => {
            const target = e.target as HTMLImageElement
            if (!isCollapsed && target.src !== '/assets/img/Actionlabs Horizontal logo 1.png') {
              target.src = '/assets/img/Actionlabs Horizontal logo 1.png'
            }
          }}
        />
        {!isCollapsed && appName ? (
          <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" flex="1" minW={0}>
            | {appName}
          </Text>
        ) : null}
      </HStack>
    </Box>
  )
})
SidebarLogo.displayName = 'SidebarLogo'

const NavIcon = memo(({ icon }: { icon: string }) => (
  <Box display="inline-flex" alignItems="center" justifyContent="center" w="20px" h="20px" flexShrink={0}>
    {renderMenuIcon(icon)}
  </Box>
))
NavIcon.displayName = 'NavIcon'

function SidebarComponent({
  items,
  isOpen = false,
  onClose,
  isCollapsed = false,
}: SidebarProps) {
  const location = useLocation()
  const pathname = location.pathname || '/'
  const navigate = useNavigate()
  const { colorMode } = useColorMode()

  const userRoutes = useAuthStore((state) => state.user?.user_routes)
  const colors = useThemeColors()
  const { settings: siteSettings } = useSiteSettings()
  const borderColor = colors.border

  const sidebarBg = colors.sidebarBg
  const sidebarText = colors.sidebarText
  const sidebarHoverBg = colors.sidebarHoverBg
  const sidebarActiveBg = colors.sidebarActiveBg

  const collapsedW = '64px'
  const expandedW = '240px'

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('sidebar-open-groups')
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, boolean>
        return parsed
      }
    } catch {
      //
    }
    return {}
  })

  const manuallyClosedRef = useRef<Set<string>>(new Set())

  const sidenavLogo = siteSettings?.sidenav_logo

  const logoUrl = useMemo(() => {
    const coloredLogo = '/assets/img/Actionlabs Horizontal logo.png'
    const whiteLogo = '/assets/img/als-white-logo.png'
    // Light mode: always use colored logo; dark mode: use site setting or white logo
    const baseUrl =
      colorMode === 'light' ? coloredLogo : (sidenavLogo || whiteLogo)
    const version = import.meta.env.DEV
      ? `?v=${baseUrl.split('/').pop()?.replace(/[^a-zA-Z0-9]/g, '') || 'dev'}`
      : `?v=${import.meta.env.VITE_BUILD_TIME || '1.0.0'}`
    return baseUrl.includes('?') ? baseUrl : `${baseUrl}${version}`
  }, [sidenavLogo, colorMode])

  const appName = useMemo(
    () => siteSettings?.site_name || import.meta.env.VITE_APP_NAME || 'ADMIN PANEL',
    [siteSettings?.site_name],
  )

  const joinPaths = useCallback((parent: string, child: string) => {
    const a = (parent || '').replace(/\/+$/, '')
    const b = (child || '').replace(/^\/+/, '')
    const joined = `${a}/${b}`
    return joined.startsWith('/') ? joined : `/${joined}`
  }, [])

  // Backend may send child path as full path (e.g. /service-catalog/service-types) or relative (e.g. /service-types)
  const getChildPath = useCallback(
    (parentPath: string, childPath: string) => {
      const p = (childPath || '').trim()
      if (!p) return parentPath
      // Full path: starts with / and has more than one segment (e.g. /service-catalog/service-types)
      if (p.startsWith('/') && p.slice(1).includes('/')) {
        return p.replace(/\/+$/, '') || '/'
      }
      return joinPaths(parentPath, childPath)
    },
    [joinPaths],
  )

  const topLevelRoutes = useMemo(() => {
    const routes = Array.isArray(userRoutes) ? userRoutes : []
    const filtered = routes.filter((r: any) => {
      const hasDisplayableChildren =
        Array.isArray(r.children) && r.children.some((c: any) => c.side_nav === 'true')
      return r.side_nav === 'true' || hasDisplayableChildren
    })
    return filtered
  }, [userRoutes])

  const isActivePath = useCallback(
    (targetPath: string) => {
      const norm = (p: string) => ('/' + (p || '').replace(/^\/+/, '')).replace(/\/+$/, '')
      const cur = norm(pathname)
      const tgt = norm(targetPath)
      return cur === tgt || cur.startsWith(tgt + '/')
    },
    [pathname],
  )

  useEffect(() => {
    const groupsToOpen: Record<string, boolean> = {}
    topLevelRoutes.forEach((route: any) => {
      const groupKey = route.path || route.name
      if (manuallyClosedRef.current.has(groupKey)) return
      if (Array.isArray(route.children)) {
        const shouldOpen = route.children.some((c: any) =>
          isActivePath(getChildPath(route.path, c.path)),
        )
        if (shouldOpen || isActivePath(route.path)) groupsToOpen[groupKey] = true
      } else if (isActivePath(route.path)) {
        groupsToOpen[groupKey] = true
      }
    })

    const keysToOpen = Object.keys(groupsToOpen)
    const soleKey = keysToOpen.length > 0 ? keysToOpen[0] : null
    const next = soleKey ? { [soleKey]: true } : {}

    setOpenGroups((prev) => {
      const prevOpen = Object.keys(prev).find((k) => prev[k])
      if ((soleKey || null) === (prevOpen || null)) return prev
      return next
    })
  }, [pathname, topLevelRoutes, isActivePath, getChildPath])

  const hasActiveChild = useCallback(
    (route: any) => {
      if (!Array.isArray(route.children)) return false
      return route.children.some((c: any) => {
        if (c.side_nav !== 'true') return false
        const childPath = getChildPath(route.path, c.path)
        return isActivePath(childPath)
      })
    },
    [isActivePath, getChildPath],
  )

  const renderNavButtons = useCallback(
    (routes: any[]) =>
      routes.map((route: any) => {
        const displayableChildren = Array.isArray(route.children)
          ? route.children.filter((c: any) => c.side_nav === 'true')
          : []
        const hasDisplayableChildren = displayableChildren.length > 0

        const shouldShowChildren = !isCollapsed && hasDisplayableChildren

        // Standalone item
        if (route.side_nav === 'true' && !hasDisplayableChildren) {
          const isRouteActive = isActivePath(route.path)
          const btn = (
            <Button
              key={route.path}
              variant="ghost"
              justifyContent={isCollapsed ? 'center' : 'flex-start'}
              alignItems="center"
              px={isCollapsed ? 1.5 : 4}
              py={isCollapsed ? 1.5 : 2}
              minH={isCollapsed ? '36px' : undefined}
              w={isCollapsed ? '100%' : undefined}
              onClick={(e) => {
                e.preventDefault()
                navigate(route.path)
                onClose?.()
              }}
              color={sidebarText}
              _hover={{ bg: sidebarHoverBg }}
              bg={isRouteActive ? sidebarActiveBg : 'transparent'}
              fontWeight={isRouteActive ? 'semibold' : 'normal'}
            >
              {isCollapsed ? (
                <NavIcon icon={route.icon} />
              ) : (
                <HStack spacing={3}>
                  <NavIcon icon={route.icon} />
                  <Text>{route.name}</Text>
                </HStack>
              )}
            </Button>
          )
          return isCollapsed ? (
            <Tooltip key={route.path} label={route.name} placement="right" hasArrow>
              <Box w="100%">{btn}</Box>
            </Tooltip>
          ) : (
            btn
          )
        }

        // Group with children
        if (hasDisplayableChildren) {
          const groupKey = route.path || route.name
          const groupIsOpen = !!openGroups[groupKey]
          const isParentActive = isActivePath(route.path) || hasActiveChild(route)

          const parentBtn = (
            <Button
              variant="ghost"
              justifyContent={isCollapsed ? 'center' : 'space-between'}
              alignItems="center"
              px={isCollapsed ? 1.5 : 4}
              py={isCollapsed ? 1.5 : 2}
              minH={isCollapsed ? '36px' : undefined}
              w="100%"
              pr={isCollapsed ? 1.5 : 4}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (isCollapsed) {
                  if (route.path) navigate(route.path)
                  onClose?.()
                  return
                }
                setOpenGroups((prev) => {
                  const newState = !prev[groupKey]
                  if (newState) {
                    manuallyClosedRef.current.delete(groupKey)
                    const updated = { [groupKey]: true }
                    try {
                      localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                    } catch {
                      //
                    }
                    return updated
                  }
                  manuallyClosedRef.current.add(groupKey)
                  const updated = { ...prev, [groupKey]: false }
                  try {
                    localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                  } catch {
                    //
                  }
                  return updated
                })
              }}
              color={sidebarText}
              _hover={{ bg: sidebarHoverBg }}
              bg={isParentActive ? sidebarActiveBg : 'transparent'}
              fontWeight={isParentActive ? 'semibold' : 'normal'}
            >
              {isCollapsed ? (
                <NavIcon icon={route.icon} />
              ) : (
                <HStack w="full" justify="space-between" spacing={3}>
                  <HStack spacing={3}>
                    <NavIcon icon={route.icon} />
                    <Text>{route.name}</Text>
                  </HStack>
                  <Icon as={groupIsOpen ? ChevronDown : ChevronRight} />
                </HStack>
              )}
            </Button>
          )

          return (
            <Box key={route.path} w="100%">
              {isCollapsed ? (
                <Tooltip label={route.name} placement="right" hasArrow>
                  <Box w="100%">{parentBtn}</Box>
                </Tooltip>
              ) : (
                parentBtn
              )}
              {shouldShowChildren ? (
                <Collapse in={groupIsOpen} animateOpacity>
                  <VStack spacing={1} pl={6} align="stretch" mt={1}>
                    {displayableChildren.map((child: any) => {
                      const childPath = getChildPath(route.path, child.path)
                      const isChildActive = isActivePath(childPath)
                      return (
                        <Button
                          key={child.path}
                          variant="ghost"
                          justifyContent="flex-start"
                          leftIcon={renderMenuIcon(child.icon)}
                          onClick={(e) => {
                            e.preventDefault()
                            navigate(childPath)
                            onClose?.()
                          }}
                          color={sidebarText}
                          _hover={{ bg: sidebarHoverBg }}
                          bg={isChildActive ? sidebarActiveBg : 'transparent'}
                          fontWeight={isChildActive ? 'semibold' : 'normal'}
                        >
                          {child.name}
                        </Button>
                      )
                    })}
                  </VStack>
                </Collapse>
              ) : null}
            </Box>
          )
        }

        return null
      }),
    [
      openGroups,
      isActivePath,
      hasActiveChild,
      getChildPath,
      navigate,
      sidebarText,
      sidebarHoverBg,
      sidebarActiveBg,
      isCollapsed,
      onClose,
    ],
  )

  const fallbackItems = useMemo(
    () =>
      (items || []).map((it) => {
        const btn = (
          <Button
            key={it.href}
            variant="ghost"
            justifyContent={isCollapsed ? 'center' : 'flex-start'}
            alignItems="center"
            px={isCollapsed ? 1.5 : 4}
            py={isCollapsed ? 1.5 : 2}
            minH={isCollapsed ? '36px' : undefined}
            w={isCollapsed ? '100%' : undefined}
            leftIcon={!isCollapsed ? renderMenuIcon(it.icon) : undefined}
            onClick={(e) => {
              e.preventDefault()
              navigate(it.href)
              onClose?.()
            }}
            color={sidebarText}
            _hover={{ bg: sidebarHoverBg }}
          >
            {isCollapsed ? <NavIcon icon={it.icon} /> : it.name}
          </Button>
        )
        return isCollapsed ? (
          <Tooltip key={it.href} label={it.name} placement="right" hasArrow>
            <Box w="100%">{btn}</Box>
          </Tooltip>
        ) : (
          btn
        )
      }),
    [items, navigate, sidebarText, sidebarHoverBg, isCollapsed, onClose],
  )

  return (
    <>
      {/* DESKTOP SIDEBAR (push layout) */}
      <Box
        w={{ base: 0, md: isCollapsed ? collapsedW : expandedW }}
        minW={{ base: 0, md: isCollapsed ? collapsedW : expandedW }}
        transition="width 0.2s ease"
        flexShrink={0}
        bg={sidebarBg}
        borderRight="1px"
        borderColor={borderColor}
        h="100vh"
        maxH="100vh"
        display={{ base: 'none', md: 'flex' }}
        flexDirection="column"
        color={sidebarText}
        overflow="hidden"
      >
        <Box flex="1" display="flex" flexDirection="column" overflow="hidden">
          <SidebarLogo logoUrl={logoUrl} appName={appName ?? ''} isCollapsed={isCollapsed} bg={sidebarBg} />

          <VStack
            spacing={isCollapsed ? 1 : 2}
            align="stretch"
            p={isCollapsed ? 2 : 4}
            pt={isCollapsed ? 1 : 2}
            pr={isCollapsed ? 1 : 2}
            px={isCollapsed ? 1 : 4}
            flex="1"
            overflowY="auto"
            overflowX="hidden"
            css={{
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(148, 163, 184, 0.35)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(148, 163, 184, 0.6)',
              },
            }}
          >
            {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
          </VStack>
        </Box>
      </Box>

      {/* MOBILE DRAWER (overlay) */}
      <Drawer isOpen={!!isOpen} placement="left" onClose={onClose || (() => {})}>
        <DrawerOverlay />
        <DrawerContent bg={sidebarBg} color={sidebarText}>
          <DrawerCloseButton />
          <DrawerHeader>
            <HStack spacing={2} align="center" overflow="hidden" w="full">
              <Image src={logoUrl} alt="ALS Logo" height="24px" objectFit="contain" flexShrink={0} />
              {appName ? (
                <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" flex="1" minW={0}>
                  | {appName}
                </Text>
              ) : null}
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}

export const Sidebar = memo(SidebarComponent, (prevProps, nextProps) => {
  let itemsEqual = prevProps.items === nextProps.items
  if (!itemsEqual && prevProps.items && nextProps.items) {
    const prevItems = prevProps.items
    const nextItems = nextProps.items
    itemsEqual =
      prevItems.length === nextItems.length &&
      prevItems.every((item, idx) => {
        const nextItem = nextItems[idx]
        return (
          nextItem &&
          item.href === nextItem.href &&
          item.name === nextItem.name &&
          item.icon === nextItem.icon
        )
      })
  }

  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.isCollapsed === nextProps.isCollapsed &&
    itemsEqual
  )
})

export default Sidebar